import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import './MobileDashboard.css';

import StackedAreaChart from './StackedAreaChart';
import LoanOverview from './LoanOverview';
import { CustomerContext } from '../App';
import { auth } from '../firebaseClient';

function formatEuro(num) {
  const val = parseFloat(num || 0);
  return '€' + val.toLocaleString();
}


function getYearOnly(dateStr) {
  if (!dateStr) return 'n/a';
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) {
    return d.getFullYear().toString();
  }
  return dateStr.slice(-4);
}


function trimToLastNonZero(incomeArr, expensesArr, monthsArr) {
  let lastIndex = -1;
  const len = Math.min(incomeArr.length, expensesArr.length, monthsArr.length);
  for (let i = len - 1; i >= 0; i--) {
    if (incomeArr[i] !== 0 || expensesArr[i] !== 0) {
      lastIndex = i;
      break;
    }
  }
  if (lastIndex === -1) {
    return {
      trimmedIncome: [],
      trimmedExpenses: [],
      trimmedMonths: [],
      lastIncome: 0,
      lastExpenses: 0,
    };
  }
  const trimmedIncome = incomeArr.slice(0, lastIndex + 1);
  const trimmedExpenses = expensesArr.slice(0, lastIndex + 1);
  const trimmedMonths = monthsArr.slice(0, lastIndex + 1);

  return {
    trimmedIncome,
    trimmedExpenses,
    trimmedMonths,
    lastIncome: trimmedIncome[trimmedIncome.length - 1] || 0,
    lastExpenses: trimmedExpenses[trimmedExpenses.length - 1] || 0,
  };
}

// -------------- Hilfsfunktionen für Transformation --------------
function transformPortfolioData(data, selectedCustomerId) {
  if (!Array.isArray(data) || !data.length) return null;
  const filtered = data.filter(row => row.customerid === selectedCustomerId);
  if (!filtered.length) return null;

  const row = filtered[0];
  const parseVal = v => parseFloat((v || '0').replace(',', '.')) || 0;
  const months = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];

  const incRaw = [
    row.portfolioincome012025, row.portfolioincome022025, row.portfolioincome032025,
    row.portfolioincome042025, row.portfolioincome052025, row.portfolioincome062025,
    row.portfolioincome072025, row.portfolioincome082025, row.portfolioincome092025,
    row.portfolioincome102025, row.portfolioincome112025, row.portfolioincome122025
  ].map(parseVal);

  const expRaw = [
    row.portfolioexpenses012025, row.portfolioexpenses022025, row.portfolioexpenses032025,
    row.portfolioexpenses042025, row.portfolioexpenses052025, row.portfolioexpenses062025,
    row.portfolioexpenses072025, row.portfolioexpenses082025, row.portfolioexpenses092025,
    row.portfolioexpenses102025, row.portfolioexpenses112025, row.portfolioexpenses122025
  ].map(parseVal);

  const { trimmedIncome, trimmedExpenses, trimmedMonths, lastIncome, lastExpenses } =
    trimToLastNonZero(incRaw, expRaw, months);

  return {
    currentValue: parseVal(row.portfoliovalue),
    purchasingValue: parseVal(row.portfoliopurchasingvalue),
    valueChangeIn: row.portfoliovaluechangein || '0%',
    occupancyRate: row.occupancyrate || 'n/a',
    purchasingDate: row.avepurchasingdate || '',
    valuationDate: row.valuationdate || '',
    numberOfProperties: parseInt(row.numberofproperties || '0', 10),

    income: trimmedIncome,
    expenses: trimmedExpenses,
    monthsUsed: trimmedMonths,
    lastMonthIncome: lastIncome,
    lastMonthExpenses: lastExpenses
  };
}

function transformCreditData(loanData, selectedCustomerId) {
  if (!Array.isArray(loanData)) return [];
  const filtered = loanData.filter(row => row.customerid === selectedCustomerId);
  return filtered.map((row) => {
    const propertyIds = (row.propertyid || '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    const objectIds = (row.objectid || '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    const interestStr = (row.interestrate || '').replace('%', '').replace(',', '.');
    const address = `${row.street || ''} ${row.number || ''}, ${row.zipcode || ''} ${row.city || ''}`.trim();

    return {
      creditId: row.creditid,
      propertyIds,
      objectIds,
      propertyName: address,
      payment: parseFloat((row.monthlypayment || '0').replace(',', '.')) || 0,
      interestRate: parseFloat(interestStr) || 0,
      remainingCredit: parseFloat((row.remainingcredit || '0').replace(',', '.')) || 0,
      duration: parseFloat((row.duration || '0').replace(',', '.')) || 0,
      yearsRemaining: parseFloat((row.yearsremaining || '0').replace(',', '.')) || 0,
    };
  });
}

function transformObjectData(dashboardObjData, overviewData, selectedCustomerId) {
  if (!Array.isArray(dashboardObjData)) return [];
  const filteredDashboard = dashboardObjData.filter(row => row.customerid === selectedCustomerId);
  const filteredOverview = overviewData.filter(ov => ov.customerid === selectedCustomerId);

  const overviewMap = new Map(filteredOverview.map(item => [item.objectid, item]));
  const months = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];
  const parseVal = v => parseFloat((v || '0').replace(',', '.')) || 0;

  return filteredDashboard.map(row => {
    const objId = row.objectid;
    const overItem = overviewMap.get(objId) || {};

    const incRaw = [
      row.objectincome012025, row.objectincome022025, row.objectincome032025,
      row.objectincome042025, row.objectincome052025, row.objectincome062025,
      row.objectincome072025, row.objectincome082025, row.objectincome092025,
      row.objectincome102025, row.objectincome112025, row.objectincome122025
    ].map(parseVal);

    const expRaw = [
      row.objectexpenses012025, row.objectexpenses022025, row.objectexpenses032025,
      row.objectexpenses042025, row.objectexpenses052025, row.objectexpenses062025,
      row.objectexpenses072025, row.objectexpenses082025, row.objectexpenses092025,
      row.objectexpenses102025, row.objectexpenses112025, row.objectexpenses122025
    ].map(parseVal);

    const { trimmedIncome, trimmedExpenses, trimmedMonths, lastIncome, lastExpenses } =
      trimToLastNonZero(incRaw, expRaw, months);

    return {
      objectId: objId,
      bezeichnung: overItem.bezeichnungimberblick || '',
      street: overItem.street || '',
      number: overItem.number || '',
      city: overItem.city || '',

      income: trimmedIncome,
      expenses: trimmedExpenses,
      monthsUsed: trimmedMonths,
      lastMonthIncome: lastIncome,
      lastMonthExpenses: lastExpenses,

      objectValue: parseVal(row.objectvalue),
      purchasingValue: parseVal(row.purchasingvalue),
      occupancyRate: row.occupancyrate || 'n/a',
      numberOfUnits: parseInt(overItem.numberofunits || '0', 10),

      objectPurchasingDate: row.objectpurchasingdate || '',
      objectValuationDate: row.objectvaluationdate || '',
      objectValueChangeIn: row.objectvaluechangein || '0%',
    };
  });
}

function transformSinglePropertyData(dashboardPropData, overviewData, selectedCustomerId) {
  if (!Array.isArray(dashboardPropData)) return [];
  const filteredDashboard = dashboardPropData.filter(row => row.customerid === selectedCustomerId);
  const filteredOverview = overviewData.filter(ov => ov.customerid === selectedCustomerId);

  const overviewMap = new Map(filteredOverview.map(item => [item.propertyid, item]));
  const months = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];
  const parseVal = v => parseFloat((v || '0').replace(',', '.')) || 0;

  return filteredDashboard.map(row => {
    const propId = row.propertyid;
    const overItem = overviewMap.get(propId) || {};

    const incRaw = [
      row.propertyincome012025, row.propertyincome022025, row.propertyincome032025,
      row.propertyincome042025, row.propertyincome052025, row.propertyincome062025,
      row.propertyincome072025, row.propertyincome082025, row.propertyincome092025,
      row.propertyincome102025, row.propertyincome112025, row.propertyincome122025
    ].map(parseVal);

    const expRaw = [
      row.propertyexpenses012025, row.propertyexpenses022025, row.propertyexpenses032025,
      row.propertyexpenses042025, row.propertyexpenses052025, row.propertyexpenses062025,
      row.propertyexpenses072025, row.propertyexpenses082025, row.propertyexpenses092025,
      row.propertyexpenses102025, row.propertyexpenses112025, row.propertyexpenses122025
    ].map(parseVal);

    const { trimmedIncome, trimmedExpenses, trimmedMonths, lastIncome, lastExpenses } =
      trimToLastNonZero(incRaw, expRaw, months);

    return {
      propertyId: propId,
      bezeichnung: overItem.bezeichnungimberblick || '',
      street: overItem.street || '',
      number: overItem.number || '',
      city: overItem.city || '',

      income: trimmedIncome,
      expenses: trimmedExpenses,
      monthsUsed: trimmedMonths,
      lastMonthIncome: lastIncome,
      lastMonthExpenses: lastExpenses,

      fullYearIncome: parseVal(row.propertyincomefullyear),
      purchasingValue: parseVal(row.purchasingvalue),
      value: parseVal(row.propertyvalue),
      purchasingDate: row.purchasingdate || '',
      valuationDate: row.valuationdate || '',
      propertyValueChangeIn: row.propertyvaluechangein || '0%',
      vermietungsrate: row.occupancyrate || 'n/a',
    };
  });
}

function MobileDashboard() {
  const { selectedCustomerId, setSelectedCustomerId } = useContext(CustomerContext);
  const [currentView, setCurrentView] = useState('portfolio');
  const location = useLocation();
  const isDemo = location.pathname.startsWith('/demo');

  // Neu: API_URL
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

  // -------------------------------------------------------------------
  // Lokale States für Portfolio
  // -------------------------------------------------------------------
  const [rawPortfolioData, setRawPortfolioData] = useState([]);
  const [portfolioData, setPortfolioData] = useState(null);
  const [portfolioCashflow, setPortfolioCashflow] = useState([]);

  const [portfolioValue, setPortfolioValue] = useState(0);
  const [portfolioPurchasingValue, setPortfolioPurchasingValue] = useState(0);
  const [portfolioValueChangeIn, setPortfolioValueChangeIn] = useState('0%');
  const [occupancyRate, setOccupancyRate] = useState('n/a');
  const [purchasingDate, setPurchasingDate] = useState('');
  const [valuationDate, setValuationDate] = useState('');
  const [portfolioLastMonthIncome, setPortfolioLastMonthIncome] = useState(0);
  const [portfolioLastMonthExpenses, setPortfolioLastMonthExpenses] = useState(0);
  const [portfolioNumUnits, setPortfolioNumUnits] = useState(0);

  // -------------------------------------------------------------------
  // Lokale States für Objekt
  // -------------------------------------------------------------------
  const [rawObjectData, setRawObjectData] = useState([]);
  const [objectOverviewData, setObjectOverviewData] = useState([]);
  const [objectData, setObjectData] = useState([]); // transformierte Objektdaten
  const [selectedObjectId, setSelectedObjectId] = useState(null);

  const [objectCashflowData, setObjectCashflowData] = useState([]);
  const [objectValue, setObjectValue] = useState(0);
  const [objectPurchasingValue, setObjectPurchasingValue] = useState(0);
  const [objectValueChangeIn, setObjectValueChangeIn] = useState('0%');
  const [objectLastMonthIncome, setObjectLastMonthIncome] = useState(0);
  const [objectLastMonthExpenses, setObjectLastMonthExpenses] = useState(0);
  const [objectOccupancyRate, setObjectOccupancyRate] = useState('n/a');
  const [objectUnits, setObjectUnits] = useState(0);
  const [objectPurchasingDate, setObjectPurchasingDate] = useState('');
  const [objectValuationDate, setObjectValuationDate] = useState('');
  const [filteredObjectLoans, setFilteredObjectLoans] = useState([]);

  // -------------------------------------------------------------------
  // Lokale States für Einheiten (Single Properties)
  // -------------------------------------------------------------------
  const [rawSingleData, setRawSingleData] = useState([]);
  const [singleOverviewData, setSingleOverviewData] = useState([]);
  const [singlePropertyData, setSinglePropertyData] = useState([]); // transformierte Einheiten
  const [selectedSinglePropertyId, setSelectedSinglePropertyId] = useState(null);

  const [cashflowData, setCashflowData] = useState([]);
  const [singleValue, setSingleValue] = useState(0);
  const [singlePurchasingValue, setSinglePurchasingValue] = useState(0);
  const [singleValueChangeIn, setSingleValueChangeIn] = useState('0%');
  const [singleLastMonthIncome, setSingleLastMonthIncome] = useState(0);
  const [singleLastMonthExpenses, setSingleLastMonthExpenses] = useState(0);
  const [singleOccupancy, setSingleOccupancy] = useState('n/a');
  const [singlePDate, setSinglePDate] = useState('');
  const [singleVDate, setSingleVDate] = useState('');
  const [singleFullYearIncome, setSingleFullYearIncome] = useState(0);
  const [filteredSingleLoans, setFilteredSingleLoans] = useState([]);

  // -------------------------------------------------------------------
  // Kredite
  // -------------------------------------------------------------------
  const [allLoans, setAllLoans] = useState([]);

  // -------------------------------------------------------------------
  // CustomerId aus Firebase
  // ABER NICHT im Demo-Modus, da dort die ID bereits von DemoRoute gesetzt wird
  // -------------------------------------------------------------------
  useEffect(() => {
    // Im Demo-Modus nicht die CustomerID aus Firebase laden
    if (isDemo) return;
    
    async function fetchCustomerId() {
      const user = auth.currentUser;
      if (!user) return;
      const email = user.email;
      try {
        const resp = await fetch(`${API_URL}/api/customerIdByEmail?email=${encodeURIComponent(email)}`);
        if (resp.ok) {
          const json = await resp.json();
          if (json.customerId) {
            setSelectedCustomerId(json.customerId);
          }
        }
      } catch (err) {
        console.error('Fehler:', err);
      }
    }
    fetchCustomerId();
  }, [API_URL, setSelectedCustomerId, isDemo]);

  // -------------------------------------------------------------------
  // Daten laden (alle Endpunkte)
  // -------------------------------------------------------------------
  useEffect(() => {
    fetch(`${API_URL}/api/dashboard-portfolio`)
      .then(r => r.json())
      .then(rows => setRawPortfolioData(rows))
      .catch(console.error);

    fetch(`${API_URL}/api/object-overview`)
      .then(r => r.json())
      .then(data => setObjectOverviewData(data))
      .catch(console.error);

    fetch(`${API_URL}/api/dashboard-object`)
      .then(r => r.json())
      .then(data => setRawObjectData(data))
      .catch(console.error);

    fetch(`${API_URL}/api/property-overview`)
      .then(r => r.json())
      .then(data => setSingleOverviewData(data))
      .catch(console.error);

    fetch(`${API_URL}/api/dashboard-property`)
      .then(r => r.json())
      .then(data => setRawSingleData(data))
      .catch(console.error);

    fetch(`${API_URL}/api/credit-overview`)
      .then(r => r.json())
      .then(loanData => setAllLoans(loanData))
      .catch(console.error);
  }, [API_URL]);

  // -------------------------------------------------------------------
  // 1) Portfolio transformieren
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!selectedCustomerId) {
      setPortfolioData(null);
      return;
    }
    const pData = transformPortfolioData(rawPortfolioData, selectedCustomerId);
    setPortfolioData(pData || null);
  }, [selectedCustomerId, rawPortfolioData]);

  useEffect(() => {
    if (!portfolioData) return;
    setPortfolioValue(portfolioData.currentValue || 0);
    setPortfolioPurchasingValue(portfolioData.purchasingValue || 0);
    setPortfolioValueChangeIn(portfolioData.valueChangeIn || '0%');
    setOccupancyRate(portfolioData.occupancyRate || 'n/a');
    setPurchasingDate(portfolioData.purchasingDate || '');
    setValuationDate(portfolioData.valuationDate || '');
    setPortfolioLastMonthIncome(portfolioData.lastMonthIncome || 0);
    setPortfolioLastMonthExpenses(portfolioData.lastMonthExpenses || 0);
    setPortfolioNumUnits(portfolioData.numberOfProperties || 0);

    const cf = portfolioData.income.map((val, idx) => ({
      month: portfolioData.monthsUsed[idx],
      income: val,
      expenses: portfolioData.expenses[idx],
    }));
    setPortfolioCashflow(cf);
  }, [portfolioData]);

  // -------------------------------------------------------------------
  // 2) Objekt transformieren
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!selectedCustomerId) return;
    if (!rawObjectData.length || !objectOverviewData.length) return;
    const merged = transformObjectData(rawObjectData, objectOverviewData, selectedCustomerId);
    setObjectData(merged);

    if (merged.length > 0) {
      setSelectedObjectId(merged[0].objectId);
    }
  }, [selectedCustomerId, rawObjectData, objectOverviewData]);

  useEffect(() => {
    if (!selectedObjectId || !objectData.length) return;
    const obj = objectData.find(o => o.objectId === selectedObjectId);
    if (!obj) return;

    setObjectValue(obj.objectValue || 0);
    setObjectPurchasingValue(obj.purchasingValue || 0);
    setObjectValueChangeIn(obj.objectValueChangeIn || '0%');
    setObjectLastMonthIncome(obj.lastMonthIncome || 0);
    setObjectLastMonthExpenses(obj.lastMonthExpenses || 0);
    setObjectOccupancyRate(obj.occupancyRate || 'n/a');
    setObjectUnits(obj.numberOfUnits || 0);
    setObjectPurchasingDate(obj.objectPurchasingDate || '');
    setObjectValuationDate(obj.objectValuationDate || '');

    const cf = obj.income.map((val, idx) => ({
      month: obj.monthsUsed[idx],
      income: val,
      expenses: obj.expenses[idx],
    }));
    setObjectCashflowData(cf);

    const filteredLoans = transformCreditData(allLoans, selectedCustomerId);
    const objLoans = filteredLoans.filter(ln => ln.objectIds.includes(selectedObjectId));
    setFilteredObjectLoans(objLoans);
  }, [selectedObjectId, objectData, allLoans, selectedCustomerId]);

  // -------------------------------------------------------------------
  // 3) Einheiten transformieren
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!selectedCustomerId) return;
    if (!rawSingleData.length || !singleOverviewData.length) return;
    const merged = transformSinglePropertyData(rawSingleData, singleOverviewData, selectedCustomerId);
    setSinglePropertyData(merged);

    if (merged.length > 0) {
      setSelectedSinglePropertyId(merged[0].propertyId);
    }
  }, [selectedCustomerId, rawSingleData, singleOverviewData]);

  useEffect(() => {
    if (!selectedSinglePropertyId || !singlePropertyData.length) return;
    const sp = singlePropertyData.find(p => p.propertyId === selectedSinglePropertyId);
    if (!sp) return;

    setSingleValue(sp.value || 0);
    setSinglePurchasingValue(sp.purchasingValue || 0);
    setSingleValueChangeIn(sp.propertyValueChangeIn || '0%');
    setSingleLastMonthIncome(sp.lastMonthIncome || 0);
    setSingleLastMonthExpenses(sp.lastMonthExpenses || 0);
    setSingleOccupancy(sp.vermietungsrate || 'n/a');
    setSinglePDate(sp.purchasingDate || '');
    setSingleVDate(sp.valuationDate || '');
    setSingleFullYearIncome(sp.fullYearIncome || 0);

    const cf = sp.income.map((val, idx) => ({
      month: sp.monthsUsed[idx],
      income: val,
      expenses: sp.expenses[idx],
    }));
    setCashflowData(cf);

    const filteredLoans = transformCreditData(allLoans, selectedCustomerId);
    const propLoans = filteredLoans.filter(ln => ln.propertyIds.includes(selectedSinglePropertyId));
    setFilteredSingleLoans(propLoans);
  }, [selectedSinglePropertyId, singlePropertyData, allLoans, selectedCustomerId]);

  return (
    <div className="mobile-dashboard-container">
      {/* View-Buttons (oben) */}
      <div className="mobile-view-buttons">
        <button
          className={currentView === 'portfolio' ? 'active' : ''}
          onClick={() => setCurrentView('portfolio')}
        >
          Portfolio
        </button>

        {/* Button nur anzeigen, wenn objectData.length > 0 */}
        {objectData.length > 0 && (
          <button
            className={currentView === 'object' ? 'active' : ''}
            onClick={() => setCurrentView('object')}
          >
            Objekt
          </button>
        )}

        <button
          className={currentView === 'single' ? 'active' : ''}
          onClick={() => setCurrentView('single')}
        >
          Einheit
        </button>
      </div>

      {/* ========== PORTFOLIO-VIEW ========== */}
      {currentView === 'portfolio' && portfolioData && (
        <section className="mobile-section">
          <h2>Portfolio Übersicht</h2>

          <div className="mobile-overview-cards">
            <div className="mobile-overview-card bw-card highlight-card">
              <div className="title">Gesamtwert</div>
              <div className="value">
                {formatEuro(portfolioValue.toFixed(0))}
              </div>
            </div>
            <div className="mobile-overview-card bw-card">
              <div className="title">Einnahmen letzter Monat</div>
              <div className="value">
                {formatEuro(portfolioLastMonthIncome.toFixed(0))}
              </div>
            </div>
            <div className="mobile-overview-card bw-card">
              <div className="title">Ausgaben letzter Monat</div>
              <div className="value">
                {formatEuro(portfolioLastMonthExpenses.toFixed(0))}
              </div>
            </div>
            <div className="mobile-overview-card bw-card">
              <div className="title">Vermietungsrate pro Jahr</div>
              <div className="value">{occupancyRate}</div>
            </div>
            <div className="mobile-overview-card bw-card">
              <div className="title">Anzahl d. Einheiten</div>
              <div className="value">{portfolioNumUnits}</div>
            </div>
          </div>

          <div className="mobile-card">
            <h3>Einnahmen & Ausgaben</h3>
            <StackedAreaChart
              data={portfolioCashflow}
              incomeLabel="Einnahmen"
              expensesLabel="Ausgaben"
            />
          </div>

          <div className="mobile-card mobile-property-value-card">
            <h3>Immobilienwert in Euro</h3>
            <div className="mobile-value-box current">
              <div className="value-label">Aktueller Wert</div>
              <div className="value-amount">
                {formatEuro(portfolioValue.toFixed(0))}
              </div>
              <div className="value-date">
                Bewertungsjahr: {getYearOnly(valuationDate)}
              </div>
            </div>
            <div className="value-change">
              <div className="change-percentage">
                {portfolioValueChangeIn.startsWith('+') 
                  ? portfolioValueChangeIn 
                  : `+${portfolioValueChangeIn}`}
              </div>
              <div className="change-arrow">↑</div>
            </div>
            <div className="mobile-value-box purchase">
              <div className="value-label">Kaufwert</div>
              <div className="value-amount">
                {formatEuro(portfolioPurchasingValue.toFixed(0))}
              </div>
              <div className="value-date">
                Kaufdatum: {purchasingDate || 'n/a'}
              </div>
            </div>
          </div>

          <div className="mobile-card">
            <h3>Kreditübersicht</h3>
            <LoanOverview loans={transformCreditData(allLoans, selectedCustomerId)} />
          </div>
        </section>
      )}

      {/* ========== OBJEKT-VIEW ========== 
          Nur anzeigen, wenn objectData vorhanden ist */}
      {currentView === 'object' && objectData.length > 0 && (
        <section className="mobile-section">
          <h2>Objekt-Ansicht</h2>

          <div className="property-selector">
            <label>Objekt:</label>
            <select
              value={selectedObjectId || ''}
              onChange={(e) => setSelectedObjectId(e.target.value)}
            >
              {objectData.map((o) => (
                <option key={o.objectId} value={o.objectId}>
                  {o.street} {o.number} ({o.bezeichnung})
                </option>
              ))}
            </select>
          </div>

          <div className="mobile-overview-cards">
            <div className="mobile-overview-card bw-card">
              <div className="title">Gesamtwert</div>
              <div className="value">{formatEuro(objectValue.toFixed(0))}</div>
            </div>
            <div className="mobile-overview-card bw-card">
              <div className="title">Einnahme (letzter Monat)</div>
              <div className="value">
                {formatEuro(objectLastMonthIncome.toFixed(0))}
              </div>
            </div>
            <div className="mobile-overview-card bw-card">
              <div className="title">Ausgabe (letzter Monat)</div>
              <div className="value">
                {formatEuro(objectLastMonthExpenses.toFixed(0))}
              </div>
            </div>
            <div className="mobile-overview-card bw-card">
              <div className="title">Vermietungsrate</div>
              <div className="value">{objectOccupancyRate}</div>
            </div>
            <div className="mobile-overview-card bw-card">
              <div className="title">Anzahl Einheiten</div>
              <div className="value">{objectUnits}</div>
            </div>
          </div>

          <div className="mobile-card">
            <h3>Objekt Cashflow</h3>
            <StackedAreaChart
              data={objectCashflowData}
              incomeLabel="Einnahmen"
              expensesLabel="Ausgaben"
            />
          </div>

          <div className="mobile-card mobile-property-value-card">
            <h3>Immobilienwert in Euro</h3>
            <div className="mobile-value-box current">
              <div className="value-label">Aktueller Wert</div>
              <div className="value-amount">
                {formatEuro(objectValue.toFixed(0))}
              </div>
              <div className="value-date">
                Bewertungsjahr: {getYearOnly(objectValuationDate)}
              </div>
            </div>
            <div className="value-change">
              <div className="change-percentage">+{objectValueChangeIn}</div>
              <div className="change-arrow">↑</div>
            </div>
            <div className="mobile-value-box purchase">
              <div className="value-label">Kaufwert</div>
              <div className="value-amount">
                {formatEuro(objectPurchasingValue.toFixed(0))}
              </div>
              <div className="value-date">
                Kaufdatum: {objectPurchasingDate || 'n/a'}
              </div>
            </div>
          </div>

          <div className="mobile-card">
            <h3>Kreditübersicht</h3>
            <LoanOverview loans={filteredObjectLoans} />
          </div>
        </section>
      )}

      {/* ========== EINHEITEN-VIEW ========== */}
      {currentView === 'single' && singlePropertyData.length > 0 && (
        <section className="mobile-section">
          <h2>Einheiten-Ansicht</h2>

          <div className="property-selector">
            <label>Einheit:</label>
            <select
              value={selectedSinglePropertyId || ''}
              onChange={(e) => setSelectedSinglePropertyId(e.target.value)}
            >
              {singlePropertyData.map((p) => (
                <option key={p.propertyId} value={p.propertyId}>
                  {p.street} {p.number} ({p.bezeichnung})
                </option>
              ))}
            </select>
          </div>

          <div className="mobile-overview-cards">
            <div className="mobile-overview-card bw-card">
              <div className="title">Gesamtwert</div>
              <div className="value">{formatEuro(singleValue.toFixed(0))}</div>
            </div>
            <div className="mobile-overview-card bw-card">
              <div className="title">Einnahme (letzter Monat)</div>
              <div className="value">
                {formatEuro(singleLastMonthIncome.toFixed(0))}
              </div>
            </div>
            <div className="mobile-overview-card bw-card">
              <div className="title">Ausgabe (letzter Monat)</div>
              <div className="value">
                {formatEuro(singleLastMonthExpenses.toFixed(0))}
              </div>
            </div>
            <div className="mobile-overview-card bw-card">
              <div className="title">Vermietungsrate</div>
              <div className="value">{singleOccupancy}</div>
            </div>
            <div className="mobile-overview-card bw-card">
              <div className="title">Kumulierte Einnahmen</div>
              <div className="value">
                {formatEuro(singleFullYearIncome.toFixed(0))}
              </div>
            </div>
          </div>

          <div className="mobile-card">
            <h3>Cashflow</h3>
            <StackedAreaChart
              data={cashflowData}
              incomeLabel="Einnahmen"
              expensesLabel="Ausgaben"
            />
          </div>

          <div className="mobile-card mobile-property-value-card">
            <h3>Immobilienwert in Euro</h3>
            <div className="mobile-value-box current">
              <div className="value-label">Aktueller Wert</div>
              <div className="value-amount">
                {formatEuro(singleValue.toFixed(0))}
              </div>
              <div className="value-date">
                Bewertungsjahr: {getYearOnly(singleVDate)}
              </div>
            </div>
            <div className="value-change">
              <div className="change-percentage">+{singleValueChangeIn}</div>
              <div className="change-arrow">↑</div>
            </div>
            <div className="mobile-value-box purchase">
              <div className="value-label">Kaufwert</div>
              <div className="value-amount">
                {formatEuro(singlePurchasingValue.toFixed(0))}
              </div>
              <div className="value-date">
                Kaufdatum: {singlePDate || 'n/a'}
              </div>
            </div>
          </div>

          <div className="mobile-card">
            <h3>Kreditübersicht</h3>
            <LoanOverview loans={filteredSingleLoans} />
          </div>
        </section>
      )}
    </div>
  );
}

export default MobileDashboard;
